import React, { useEffect, useState } from 'react';
import { getAdminAnnotations, getApprovedEmails, manageApprovedEmail, markMissed, bulkDismiss } from '../api/client';

interface Annotation {
  submission_id: string;
  annotator_nickname: string;
  expert_grade: number | string;
  expert_label: string;
  confidence: string;
  notes: string;
  created_at: string;
}

interface ApprovedEmail {
  email: string;
  added_by: string;
  added_at: string;
  note: string;
}

interface Props {
  onReview: (id: string) => void;
}

export default function AdminPanel({ onReview }: Props) {
  const [tab, setTab] = useState<'audit' | 'access'>('access');

  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [annotationsLoading, setAnnotationsLoading] = useState(false);
  const [annotationsError, setAnnotationsError] = useState<string | null>(null);
  const [filterAnnotator, setFilterAnnotator] = useState('');

  const [approvedEmails, setApprovedEmails] = useState<ApprovedEmail[]>([]);
  const [accessLoading, setAccessLoading] = useState(true);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [newNote, setNewNote] = useState('');
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const [missedDate, setMissedDate] = useState('2026-07-03');
  const [missedSaving, setMissedSaving] = useState(false);
  const [missedResult, setMissedResult] = useState<string | null>(null);
  const [missedError, setMissedError] = useState<string | null>(null);

  const [bulkGuess, setBulkGuess] = useState(9.0);
  const [bulkDivergence, setBulkDivergence] = useState(2.0);
  const [bulkPreview, setBulkPreview] = useState<number | null>(null);
  const [bulkWorking, setBulkWorking] = useState(false);
  const [bulkResult, setBulkResult] = useState<string | null>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);

  // Load approved emails on mount
  useEffect(() => {
    setAccessLoading(true);
    getApprovedEmails()
      .then(d => setApprovedEmails(d.approved_emails ?? []))
      .catch(e => setAccessError(e.message))
      .finally(() => setAccessLoading(false));
  }, []);

  // Load annotations lazily when tab is opened
  useEffect(() => {
    if (tab !== 'audit' || annotations.length > 0) return;
    setAnnotationsLoading(true);
    getAdminAnnotations()
      .then(d => setAnnotations(d.annotations ?? []))
      .catch(e => setAnnotationsError(e.message))
      .finally(() => setAnnotationsLoading(false));
  }, [tab]);

  const handleAdd = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    setAddSaving(true);
    setAddError(null);
    try {
      await manageApprovedEmail('add', email, newNote.trim());
      setApprovedEmails(prev => [
        { email, added_by: 'you', added_at: new Date().toISOString(), note: newNote.trim() },
        ...prev,
      ]);
      setNewEmail('');
      setNewNote('');
    } catch (e: any) {
      setAddError(e.message);
    } finally {
      setAddSaving(false);
    }
  };

  const handleBulkPreview = async () => {
    setBulkWorking(true);
    setBulkPreview(null);
    setBulkError(null);
    setBulkResult(null);
    try {
      const r = await bulkDismiss(bulkGuess, bulkDivergence, true);
      setBulkPreview(r.count);
    } catch (e: any) {
      setBulkError(e.message);
    } finally {
      setBulkWorking(false);
    }
  };

  const handleBulkDismiss = async () => {
    if (bulkPreview === null) return;
    if (!window.confirm(`Mark ${bulkPreview} submissions as Missed? They'll move to the Missed tab and stay out of the training queue.`)) return;
    setBulkWorking(true);
    setBulkError(null);
    try {
      const r = await bulkDismiss(bulkGuess, bulkDivergence, false);
      setBulkResult(`Done — ${r.dismissed} submission${r.dismissed === 1 ? '' : 's'} moved to Missed.`);
      setBulkPreview(null);
    } catch (e: any) {
      setBulkError(e.message);
    } finally {
      setBulkWorking(false);
    }
  };

  const handleMarkMissed = async () => {
    if (!missedDate) return;
    if (!window.confirm(`Mark all PENDING_REVIEW submissions older than ${missedDate} as Missed? This cannot be undone easily.`)) return;
    setMissedSaving(true);
    setMissedResult(null);
    setMissedError(null);
    try {
      const r = await markMissed(missedDate);
      setMissedResult(`Done — ${r.marked_missed} submission${r.marked_missed === 1 ? '' : 's'} marked as Missed.`);
    } catch (e: any) {
      setMissedError(e.message);
    } finally {
      setMissedSaving(false);
    }
  };

  const handleRemove = async (email: string) => {
    if (!window.confirm(`Remove ${email} from the approved list?`)) return;
    try {
      await manageApprovedEmail('remove', email);
      setApprovedEmails(prev => prev.filter(e => e.email !== email));
    } catch (e: any) {
      setAccessError(e.message);
    }
  };

  const annotatorNames = Array.from(new Set(annotations.map(a => a.annotator_nickname))).sort();
  const visibleAnnotations = filterAnnotator
    ? annotations.filter(a => a.annotator_nickname === filterAnnotator)
    : annotations;

  const gradeDisplay = (a: Annotation) =>
    a.expert_grade === 'ng' ? 'NG' : typeof a.expert_grade === 'number' ? a.expert_grade.toFixed(1) : String(a.expert_grade);

  const confidenceColor = (c: string) =>
    c === 'high' ? '#4ac47a' : c === 'medium' ? '#c4a84a' : '#e07070';

  return (
    <div className="admin-panel">
      <div className="admin-tabs">
        <button
          className={`admin-tab-btn ${tab === 'access' ? 'admin-tab-active' : ''}`}
          onClick={() => setTab('access')}
        >
          Access Control
        </button>
        <button
          className={`admin-tab-btn ${tab === 'audit' ? 'admin-tab-active' : ''}`}
          onClick={() => setTab('audit')}
        >
          Annotation Audit
        </button>
      </div>

      {tab === 'access' && (
        <div className="access-section">
          <div className="access-add">
            <h3>Bulk dismiss lazy grade guesses</h3>
            <p className="access-hint">Moves PENDING_REVIEW submissions where the user guessed a specific grade (e.g. 9.0) and the divergence meets a threshold to Missed. The photos and AI grade stay intact for future use.</p>
            <div className="access-add-row">
              <label className="access-inline-label">User guessed</label>
              <select
                className="access-email-input"
                style={{ width: 90 }}
                value={bulkGuess}
                onChange={e => { setBulkGuess(parseFloat(e.target.value)); setBulkPreview(null); }}
              >
                {[10.0,9.8,9.6,9.4,9.2,9.0,8.5,8.0,7.5,7.0,6.5,6.0].map(g => (
                  <option key={g} value={g}>{g.toFixed(1)}</option>
                ))}
              </select>
              <label className="access-inline-label">min gap</label>
              <select
                className="access-email-input"
                style={{ width: 80 }}
                value={bulkDivergence}
                onChange={e => { setBulkDivergence(parseFloat(e.target.value)); setBulkPreview(null); }}
              >
                {[1.0,1.5,2.0,2.5,3.0,4.0,5.0].map(d => (
                  <option key={d} value={d}>≥ {d.toFixed(1)}</option>
                ))}
              </select>
              <button className="access-add-btn" onClick={handleBulkPreview} disabled={bulkWorking}>
                {bulkWorking && bulkPreview === null ? 'Checking...' : 'Preview'}
              </button>
              {bulkPreview !== null && (
                <button
                  className="access-add-btn"
                  style={{ background: '#7a4000' }}
                  onClick={handleBulkDismiss}
                  disabled={bulkWorking || bulkPreview === 0}
                >
                  {bulkWorking ? 'Working...' : `Dismiss ${bulkPreview}`}
                </button>
              )}
            </div>
            {bulkPreview !== null && !bulkResult && (
              <div className="access-hint" style={{ marginTop: 6, color: bulkPreview === 0 ? '#888' : '#c4a84a' }}>
                {bulkPreview === 0 ? 'No matching submissions found.' : `${bulkPreview} submissions would be moved to Missed.`}
              </div>
            )}
            {bulkResult && <div className="success-banner">{bulkResult}</div>}
            {bulkError && <div className="access-error">{bulkError}</div>}
          </div>

          <div className="access-add">
            <h3>Mark old submissions as Missed</h3>
            <p className="access-hint">Moves all Pending Review submissions older than the selected date out of the queue. They'll appear in the Missed tab on the dashboard.</p>
            <div className="access-add-row">
              <input
                type="date"
                className="access-email-input"
                value={missedDate}
                onChange={e => setMissedDate(e.target.value)}
              />
              <button
                className="access-add-btn"
                onClick={handleMarkMissed}
                disabled={!missedDate || missedSaving}
              >
                {missedSaving ? 'Working...' : 'Mark as Missed'}
              </button>
            </div>
            {missedResult && <div className="success-banner">{missedResult}</div>}
            {missedError && <div className="access-error">{missedError}</div>}
          </div>

          <div className="access-add">
            <h3>Approve a new user</h3>
            <p className="access-hint">Add their email before they try to log in. Works for Google and email accounts.</p>
            <div className="access-add-row">
              <input
                className="access-email-input"
                type="email"
                placeholder="email@example.com"
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
              />
              <input
                className="access-note-input"
                type="text"
                placeholder="Note (optional)"
                value={newNote}
                onChange={e => setNewNote(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
              />
              <button
                className="access-add-btn"
                onClick={handleAdd}
                disabled={!newEmail.trim() || addSaving}
              >
                {addSaving ? 'Adding...' : 'Approve'}
              </button>
            </div>
            {addError && <div className="access-error">{addError}</div>}
          </div>

          {accessError && <div className="error-banner">{accessError}</div>}
          {accessLoading ? (
            <div className="loading">Loading approved users...</div>
          ) : (
            <div className="approved-list">
              <h3>Approved users ({approvedEmails.length})</h3>
              {approvedEmails.length === 0 && (
                <div className="empty-state">No approved users yet.</div>
              )}
              {approvedEmails.map(e => (
                <div key={e.email} className="approved-row">
                  <div className="approved-email">{e.email}</div>
                  {e.note && <div className="approved-note">{e.note}</div>}
                  <div className="approved-meta">
                    Added {e.added_at?.slice(0, 10)} by {e.added_by}
                  </div>
                  <button
                    className="approved-remove-btn"
                    onClick={() => handleRemove(e.email)}
                  >
                    Revoke
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'audit' && (
        <div>
          {annotationsLoading && <div className="loading">Loading audit log...</div>}
          {annotationsError && <div className="error-banner">{annotationsError}</div>}
          {!annotationsLoading && !annotationsError && (
            <>
              <div className="admin-controls">
                <span className="admin-count">{visibleAnnotations.length} annotations</span>
                <select
                  value={filterAnnotator}
                  onChange={e => setFilterAnnotator(e.target.value)}
                  className="admin-filter"
                >
                  <option value="">All graders</option>
                  {annotatorNames.map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
              <div className="audit-table-wrap">
                <table className="audit-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Submission</th>
                      <th>Grader</th>
                      <th>Grade</th>
                      <th>Label</th>
                      <th>Confidence</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleAnnotations.map((a, i) => (
                      <tr key={i} className="audit-row">
                        <td className="audit-date">{a.created_at?.slice(0, 10)}</td>
                        <td>
                          <button className="audit-sub-link" onClick={() => onReview(a.submission_id)}>
                            {a.submission_id.slice(0, 24)}…
                          </button>
                        </td>
                        <td className="audit-grader">{a.annotator_nickname}</td>
                        <td className="audit-grade">{gradeDisplay(a)}</td>
                        <td className="audit-label">{a.expert_label}</td>
                        <td>
                          <span className="audit-confidence" style={{ color: confidenceColor(a.confidence) }}>
                            {a.confidence}
                          </span>
                        </td>
                        <td className="audit-notes">{a.notes || <span className="audit-empty">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
